import { GoBackHeaderTitle, If } from "components";
import { toastServerError } from "helpers";
import { BoxShadowLayout, ContentLayout } from "layouts";
import { StaffManagerPermissions, useProfile } from "modules/common";
import { useMemo } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import { Button } from "ui";

import { useAcknowledgeAuditAlert, useGetAuditAlertById } from "../../apis";
import { AuditAlertResponse } from "../../auditLog.types";
import { AuditAlertDetailContent } from "../../components";

import styles from "./AuditAlertDetail.module.scss";

const AuditAlertDetail = () => {
  const { t } = useTranslation();
  const { alertId } = useParams();
  const navigate = useNavigate();

  const { auditAlert, isLoading, refetch } = useGetAuditAlertById(
    alertId as string
  );

  const { user } = useProfile({ enabled: false });
  const { canAcknowledge } = useMemo(() => {
    return {
      canAcknowledge:
        user.role === "Super" ||
        user.permissions?.includes(
          StaffManagerPermissions.AcknowledgeAuditAlerts
        )
    };
  }, [user]);

  const { mutate: acknowledge, isLoading: isAcknowledging } =
    useAcknowledgeAuditAlert({
      onSuccess: () => {
        toast.success(t("Alert marked as reviewed"));
      },
      onError: (error) => {
        toastServerError(error);
        void refetch();
      }
    });

  const handleAcknowledge = () => {
    if (alertId) acknowledge(alertId);
  };

  const action = auditAlert?.isAcknowledged ? (
    <Button variant="default" disabled>
      {t("Reviewed")}
    </Button>
  ) : auditAlert && canAcknowledge ? (
    <Button onClick={handleAcknowledge} loading={isAcknowledging}>
      {t("Mark as reviewed")}
    </Button>
  ) : null;

  return (
    <ContentLayout
      title={
        <GoBackHeaderTitle
          title={t("Critical Action Alert")}
          onGoBack={() => navigate(-1)}
        />
      }
      action={action}
    >
      <BoxShadowLayout className={styles.detail}>
        <If condition={isLoading}>
          <div className={styles.detail__loader}>
            <ClipLoader size={30} color="#fb3973" />
          </div>
        </If>

        <If condition={!isLoading && !!auditAlert}>
          <AuditAlertDetailContent
            auditAlert={auditAlert as AuditAlertResponse}
          />
        </If>

        <If condition={!isLoading && !auditAlert}>
          <p className={styles.detail__notFound}>{t("Alert not found")}</p>
        </If>
      </BoxShadowLayout>
    </ContentLayout>
  );
};

export default AuditAlertDetail;
